import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import NewDossierDialog from "@/components/NewDossierDialog";
import CommandoToday from "@/components/CommandoToday";
import CommandoBusiness from "@/components/CommandoBusiness";
import CommandoV11 from "@/components/CommandoV11";
import { isDAv2 } from "@/lib/da";
import { SuiviApporteur, SuiviApporteurDossier, SuiviClient, DossiersAReprendre } from "@/components/CommandoBlocks";
import { Plus, Target } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * V4.20 — Commando = Centre d'exécution quotidien.
 *
 * Doctrine produit :
 *   • Commando  = exécution immédiate. "Que dois-je faire maintenant ?"
 *   • Pilotage  = analyse stratégique. "Pourquoi et comment optimiser ?"
 *
 * Nouvelle architecture (5 blocs principaux mobile-first via CommandoToday) :
 *   1. Hero Objectif du jour
 *   2. Priorité unique
 *   3. Relances critiques (multi-source, action sur place)
 *   4. Dossiers chauds (CA potentiel + maturité)
 *   5. Santé pipeline (compact)
 *
 * Blocs auxiliaires conservés (angles distincts non couverts par les 5 ci-dessus) :
 *   • Dossiers à reprendre (stand-by arrivés à terme)
 *   • Suivi apporteur (apporteur-dossier post-relance)
 *   • Suivi apporteur (recontact selon Tier)
 *   • Suivi client (post-signature)
 */
export default function DashboardCommandoDual() {
  const [showNew, setShowNew] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const bump = () => setRefreshKey((k) => k + 1);
  void bump; // keep ref for child Suivi blocks that don't auto-refresh

  return (
    <Layout
      title={user?.name || "Courtier"}
      subtitle={
        <span className="flex items-center gap-1.5">
          <Target size={12} weight="fill" color="var(--cf-warning)" /> Mode Commando
        </span>
      }
      headerRight={
        <button
          onClick={() => setShowNew(true)}
          className="w-9 h-9 rounded-full bg-[color:var(--cf-primary)] text-white flex items-center justify-center shadow-[0_4px_12px_rgba(45,110,255,0.3)]"
          data-testid="mobile-new-dossier"
          aria-label="Nouveau dossier"
        >
          <Plus size={18} weight="bold" />
        </button>
      }
    >
      <div className="hidden md:flex items-center justify-end mb-3">
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 bg-[color:var(--cf-primary)] hover:bg-[color:var(--cf-primary-hover)] text-white rounded-lg px-3 py-1.5 text-xs font-bold"
          data-testid="desktop-new-dossier"
        >
          <Plus size={13} weight="bold" /> Nouveau dossier
        </button>
      </div>

      {isDAv2() ? (
        /* V11 — Commando intelligent (DA v2 uniquement) : 3 blocs, le reste dans /commando/recommandations */
        <CommandoV11 refreshKey={refreshKey} />
      ) : (
        <>
          {/* V10 — Commando Business : Trésorerie › Objectif › Plan (en tête, blocs historiques conservés dessous) */}
          <CommandoBusiness refreshKey={refreshKey} />

          {/* V4.20 — 5 blocs principaux */}
          <CommandoToday refreshKey={refreshKey} />

          {/* Blocs auxiliaires — angles distincts (Safe = Silence si rien à faire) */}
          <DossiersAReprendre refreshKey={refreshKey} />
          <SuiviApporteurDossier refreshKey={refreshKey} />
          <SuiviApporteur refreshKey={refreshKey} />
          <SuiviClient refreshKey={refreshKey} />
        </>
      )}

      <NewDossierDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(dossier) => {
          toast.success("Dossier créé");
          navigate(`/dossier/${dossier.id}`);
        }}
      />
    </Layout>
  );
}
